import { authService } from '@bri/shared-components';

/**
 * Billing Service Client
 * Handles all billing and subscription API calls
 */

export interface SubscriptionTier {
  name: string;
  tier: 'FREE' | 'PRO' | 'ENTERPRISE';
  price: string;
  interval?: string;
  credits: number;
  features: {
    label: string;
    included: boolean;
    value?: string;
  }[];
}

export interface Subscription {
  id: string;
  user_id: string;
  stripe_subscription_id: string;
  plan_type: string;
  status: string;
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
}

export interface UserCredits {
  totalCreditsOwned: number;
  creditsAllocatedToWorkspaces: number;
  creditsAvailable: number;
  monthlyCreditsAllotment: number;
  subscriptionTier: string;
}

export interface CreditPack {
  id: string;
  name: string;
  credits: number;
  price: number;
  stripePriceId: string;
  stripeProductId: string;
}

export interface WorkspaceCredits {
  workspaceId: string;
  workspaceName: string;
  totalCredits: number;
  creditsRemaining: number;
  creditsUsedThisMonth: number;
  byService: {
    qualAnalyzer: number;
    fileStorage: number;
    analytics: number;
    api: number;
  };
}

export interface CreditAllocation {
  id: string;
  user_id: string;
  workspace_id: string;
  credit_type: string;
  amount: number;
  status: string;
  allocated_at: string;
  workspace_name?: string;
}

export interface Transaction {
  id: string;
  user_id: string;
  workspace_id?: string;
  type: string;
  transaction_type: string;
  credit_amount?: number;
  amount?: number;
  currency?: string;
  status: string;
  service_source?: string;
  action?: string;
  balance_after?: number;
  metadata?: Record<string, unknown>;
  created_at: string;
}

interface BillingApiResponse<TData> {
  success: boolean;
  data: TData;
  error?: string;
}

interface SubscriptionPaymentIntentPayload {
  paymentIntent: {
    clientSecret: string;
  };
  tier: {
    name: string;
    tier: 'PRO' | 'ENTERPRISE';
    price: string;
    credits: number;
  };
}

interface CreditPurchasePayload {
  paymentIntent: {
    clientSecret: string;
  };
  pack: CreditPack;
}

class BillingService {
  private baseUrl = import.meta.env.VITE_BILLING_API_URL || 'https://7u8c1u57x3.execute-api.us-east-1.amazonaws.com/dev';

  private async getAuthToken(): Promise<string> {
    return authService.getValidToken();
  }

  /**
   * Make authenticated request to billing API
   * Note: We can't use authService.apiRequest because it prepends the auth service URL
   */
  private async request<TResponse>(endpoint: string, options: RequestInit = {}): Promise<TResponse> {
    const token = await this.getAuthToken();

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      console.error('Billing API error:', error);
      throw new Error(error.error || `Request failed: ${response.status}`);
    }

    return response.json() as Promise<TResponse>;
  }

  // ==================================================================
  // SUBSCRIPTION MANAGEMENT
  // ==================================================================

  async getSubscription(): Promise<BillingApiResponse<{ subscription: Subscription | null }>> {
    return this.request('/billing/subscription', {
      method: 'GET',
    });
  }

  async testSubscribe(tier: 'PRO' | 'ENTERPRISE') {
    return this.request('/billing/test/subscribe', {
      method: 'POST',
      body: JSON.stringify({ tier }),
    });
  }

  async createSubscription(tier: 'PRO' | 'ENTERPRISE', paymentMethodId?: string) {
    return this.request('/billing/subscribe', {
      method: 'POST',
      body: JSON.stringify({
        tier,
        paymentMethodId,
      }),
    });
  }

  async createSubscriptionPaymentIntent(tier: 'PRO' | 'ENTERPRISE'): Promise<BillingApiResponse<SubscriptionPaymentIntentPayload>> {
    return this.request('/billing/subscribe/payment-intent', {
      method: 'POST',
      body: JSON.stringify({
        tier,
      }),
    });
  }

  async updateSubscription(tier: 'PRO' | 'ENTERPRISE') {
    return this.request('/billing/subscription', {
      method: 'PUT',
      body: JSON.stringify({ tier }),
    });
  }

  async cancelSubscription() {
    return this.request('/billing/subscription', {
      method: 'DELETE',
    });
  }

  // ==================================================================
  // CREDIT MANAGEMENT
  // ==================================================================

  async getUserCredits(): Promise<UserCredits> {
    const response = await this.request<BillingApiResponse<UserCredits>>('/billing/credits', {
      method: 'GET',
    });
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch credits');
    }
    
    return response.data;
  }

  async getCreditPacks(): Promise<CreditPack[]> {
    const response = await this.request<BillingApiResponse<{ packs: CreditPack[] }>>('/billing/credits/packs', {
      method: 'GET',
    });
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch credit packs');
    }
    
    return response.data.packs;
  }

  async purchaseCredits(packId: string, paymentMethodId?: string): Promise<BillingApiResponse<CreditPurchasePayload>> {
    return this.request('/billing/credits/purchase', {
      method: 'POST',
      body: JSON.stringify({
        packId,
        paymentMethodId,
      }),
    });
  }

  async testPurchaseCredits(packId: string) {
    return this.request('/billing/test/purchase', {
      method: 'POST',
      body: JSON.stringify({ packId }),
    });
  }

  // ==================================================================
  // CREDIT ALLOCATION (User -> Workspace)
  // ==================================================================

  async getAllocations(): Promise<CreditAllocation[]> {
    const response = await this.request<BillingApiResponse<{ allocations: CreditAllocation[] }>>('/billing/credits/allocations', {
      method: 'GET',
    });
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch allocations');
    }
    
    return response.data.allocations;
  }

  async allocateCredits(workspaceId: string, credits: number) {
    return this.request('/billing/credits/allocate', {
      method: 'POST',
      body: JSON.stringify({
        workspaceId,
        credits,
      }),
    });
  }

  async revokeCredits(workspaceId: string, credits?: number) {
    return this.request('/billing/credits/revoke', {
      method: 'POST',
      body: JSON.stringify({
        workspaceId,
        credits,
      }),
    });
  }

  async getTransactionHistory(limit: number = 100): Promise<Transaction[]> {
    const response = await this.request<BillingApiResponse<{ transactions: Transaction[] }>>(`/billing/transactions?limit=${limit}`, {
      method: 'GET',
    });
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch transaction history');
    }
    
    return response.data.transactions;
  }

  // ==================================================================
  // WORKSPACE CREDITS
  // ==================================================================

  async getWorkspaceCredits(workspaceId: string): Promise<WorkspaceCredits> {
    const response = await this.request<BillingApiResponse<WorkspaceCredits>>(`/billing/workspace/${workspaceId}/credits`, {
      method: 'GET',
    });
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch workspace credits');
    }
    
    return response.data;
  }

  async getWorkspaceUsage(workspaceId: string, period: 'month' | 'all' = 'month') {
    return this.request(`/billing/workspace/${workspaceId}/usage?period=${period}`, {
      method: 'GET',
    });
  }

  // ==================================================================
  // PAYMENT METHODS
  // ==================================================================

  async getPaymentMethods() {
    return this.request('/billing/payment-methods', {
      method: 'GET',
    });
  }

  async addPaymentMethod(paymentMethodId: string, setAsDefault: boolean = true) {
    return this.request('/billing/payment-method', {
      method: 'POST',
      body: JSON.stringify({
        paymentMethodId,
        setAsDefault,
      }),
    });
  }

  async removePaymentMethod(paymentMethodId: string) {
    return this.request(`/billing/payment-method/${paymentMethodId}`, {
      method: 'DELETE',
    });
  }

  // ==================================================================
  // CUSTOMER PORTAL
  // ==================================================================

  async getPortalUrl(returnUrl?: string): Promise<string> {
    const endpoint = returnUrl
      ? `/billing/portal?returnUrl=${encodeURIComponent(returnUrl)}`
      : '/billing/portal';
    
    const response = await this.request<BillingApiResponse<{ url: string }>>(endpoint, { method: 'GET' });
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to get portal URL');
    }
    
    return response.data.url;
  }

  // ==================================================================
  // SUBSCRIPTION TIERS (Static Configuration)
  // ==================================================================

  getSubscriptionTiers(): SubscriptionTier[] {
    return [
      {
        name: 'Free',
        tier: 'FREE',
        price: '$0',
        credits: 10,
        features: [
          { label: 'Workspaces', included: true, value: '1' },
          { label: 'Users per workspace', included: true, value: '3' },
          { label: 'Storage', included: true, value: '5 GB' },
          { label: 'Monthly credits', included: true, value: '10' },
          { label: 'Qualitative Analyzer', included: false },
          { label: 'Advanced Analytics', included: false },
          { label: 'API Access', included: false },
        ],
      },
      {
        name: 'Pro',
        tier: 'PRO',
        price: '$49',
        interval: 'month',
        credits: 100,
        features: [
          { label: 'Workspaces', included: true, value: '5' },
          { label: 'Users per workspace', included: true, value: '10' },
          { label: 'Storage', included: true, value: '100 GB' },
          { label: 'Monthly credits', included: true, value: '100' },
          { label: 'Qualitative Analyzer', included: true },
          { label: 'Advanced Analytics', included: true },
          { label: 'API Access', included: true },
        ],
      },
      {
        name: 'Enterprise',
        tier: 'ENTERPRISE',
        price: '$199',
        interval: 'month',
        credits: 2000,
        features: [
          { label: 'Unlimited workspaces', included: true },
          { label: 'Unlimited users', included: true },
          { label: 'Storage', included: true, value: '5 TB' },
          { label: 'Monthly credits', included: true, value: '2000+' },
          { label: 'Qualitative Analyzer', included: true },
          { label: 'Advanced Analytics', included: true },
          { label: 'API Access', included: true },
          { label: 'Dedicated support', included: true },
          { label: 'Custom integrations', included: true },
        ],
      },
    ];
  }

  /**
   * Run database migration (Admin only)
   * This fixes workspace_id column types from UUID to TEXT
   */
  async runMigration() {
    return this.request('/billing/admin/run-migration', {
      method: 'POST',
    });
  }
}

export const billingService = new BillingService();

